import React, { useMemo } from "react";
import cx from "classnames";
import { groupBy } from "lodash";

import { Outcome } from "./Outcome";
import { HeadToHeadRow } from "./HeadToHeadRow";
import { getOutcomeCompetitor, type OutcomeLayoutProps } from "./outcomeLayout";

import type { ProcessedOutcomes } from "@/hooks/firestore/betting/useEvent";

import * as styles from "./Outcomes.module.scss";

type GroupRow =
  | { drawOutcome: ProcessedOutcomes[string]; drawId: string }
  | { homeId: string; awayId: string };

// the margin key an outcome groups under: its abbreviation, or a fallback for
// the outcomes that ship without one
const getGroupKey = (outcome: ProcessedOutcomes[string]): string => {
  if (outcome.abbreviation) return outcome.abbreviation;

  // draw has no abbreviation, and it gets a row of its own
  if (outcome.type === "DRAW") return "draw";

  // fall back to the last part of the name (e.g, "3+", "1", "1-7")
  const nameParts = outcome.name?.split(" ");
  if (nameParts && nameParts.length > 0) return nameParts.slice(-1)[0];

  // won't be displayed, since we can't render it as h2h
  return outcome.name ?? "";
};

// groups outcomes by margin (abbreviation) - null if a group can't pair
// cleanly, so the market doesn't fit this layout
const buildGroupRows = (outcomes: ProcessedOutcomes): GroupRow[] | null => {
  const grouped = groupBy(Object.entries(outcomes), ([, outcome]) =>
    getGroupKey(outcome),
  );

  // sort the outcomes by key (abbreviation or name)
  const orderedKeys = Object.keys(grouped).sort(
    new Intl.Collator("en", { numeric: true }).compare,
  );

  const rows: GroupRow[] = [];

  for (const key of orderedKeys) {
    const entries = grouped[key];
    if (!entries) continue;

    const draw = entries.find(([, outcome]) => outcome.type === "DRAW");
    if (key === "draw" && draw) {
      rows.push({ drawId: draw[0], drawOutcome: draw[1] });
      continue;
    }

    const home = entries.find(([, outcome]) => outcome.type?.endsWith("_HOME"));
    const away = entries.find(([, outcome]) => outcome.type?.endsWith("_AWAY"));
    if (home && away) {
      rows.push({ homeId: home[0], awayId: away[0] });
      continue;
    }

    return null;
  }

  return rows;
};

// checked before rendering, so an unpairable market falls through to StackedOutcomes
export const canRenderWinningMargin = (outcomes: ProcessedOutcomes): boolean =>
  buildGroupRows(outcomes) !== null;

const WinningMarginOutcomes = ({
  outcomes,
  competitors,
  eventId,
  eventStatus,
  marketId,
  oddsFormat,
  selections,
  toggleSelection,
  className,
  isSuspended = false,
  isOutright = false,
  status,
  isLoading = false,
  mainMarket,
  renderFieldsKey,
}: OutcomeLayoutProps) => {
  // callers check canRenderWinningMargin first - null here shouldn't happen
  const groupRows = useMemo(() => buildGroupRows(outcomes) ?? [], [outcomes]);

  const isDrawLast = "drawId" in (groupRows[groupRows.length - 1] ?? {});
  // the last home/away row closes off the card
  const lastHomeAwayRow = groupRows.length - (isDrawLast ? 2 : 1);

  return (
    <div className={cx(styles.outcomes, className)}>
      {groupRows.map((row, index) => {
        if ("drawId" in row) {
          return (
            <Outcome
              className={cx(styles.outcome, styles.halfWidth)}
              align="right"
              key={row.drawId}
              id={row.drawId}
              eventId={eventId}
              eventStatus={eventStatus}
              marketId={marketId}
              outcome={row.drawOutcome}
              oddsFormat={oddsFormat}
              competitor={getOutcomeCompetitor(
                competitors,
                row.drawOutcome,
                mainMarket,
              )}
              toggleSelection={toggleSelection}
              isSuspended={isSuspended || !row.drawOutcome.active}
              isOutright={isOutright}
              isSelected={selections.includes(row.drawId)}
              edges={{
                roundBottomLeft: isDrawLast,
                dividerRight: true,
              }}
              status={status}
              isLoading={isLoading}
              renderFieldsKey={renderFieldsKey}
            />
          );
        }

        return (
          <HeadToHeadRow
            key={`${row.homeId}-${row.awayId}`}
            outcomes={outcomes}
            competitors={competitors}
            eventId={eventId}
            eventStatus={eventStatus}
            marketId={marketId}
            oddsFormat={oddsFormat}
            selections={selections}
            toggleSelection={toggleSelection}
            isSuspended={isSuspended}
            isOutright={isOutright}
            status={status}
            isLoading={isLoading}
            mainMarket={mainMarket}
            homeOutcomeId={row.homeId}
            awayOutcomeId={row.awayId}
            roundBottom={index === lastHomeAwayRow && !isDrawLast}
            bottomBorder={index === lastHomeAwayRow && isDrawLast}
            renderFieldsKey={renderFieldsKey}
          />
        );
      })}
    </div>
  );
};

export { WinningMarginOutcomes };
